import { FC, useCallback } from "react";

import { useAssessedCountData, useAssessmentMode } from "../../../../utils/assessment";
import { useQueryParam, useQueryParamsActions } from "../../../../utils/routing";

import { AssessmentStatus } from "api/api-data-types";
import { SECONDARY_OVERLAY_QUERY_PARAM, SecondaryOverlay } from "components/constants";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import type { ResourceKey } from "i18n/i18next";
import { Banner, BannerProps } from "mds/components/Banner";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { AssessPointsMarkerIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectAssessmentsByPageId,
  selectCurrentSectionIsArchived,
  selectIsDerivingFeedbackStyle,
  selectIsRecordingFeedbackStyle,
  selectIsUnassessablePage,
  selectOfficialFullSubmissionsByPageId,
  selectSubmissionsByPageId,
} from "store/selectors";
import { selectAssessmentInfoForPage } from "store/selectors/computed";
import { viewStateActions } from "store/slices/view";
import { useListSelector } from "store/store-hooks";
import { stopRecordingFeedbackStyle } from "utils/assessment-feedback-style-recording";
import { isGroupPage, isIndividualPage } from "utils/page-utils";
import { isAssessmentAIFeedbackStyleEnabled } from "utils/waffle-flags";

const RecordingFeedbackStyleBanner: FC = () => {
  const dispatch = useAppDispatch();
  const isDeriving = useAppSelector(selectIsDerivingFeedbackStyle);
  const onStopRecording = useCallback(async () => {
    if (isDeriving) {
      return;
    }
    try {
      await stopRecordingFeedbackStyle(dispatch);
    } catch {
      // Toasts in stopRecordingFeedbackStyle
    }
  }, [dispatch, isDeriving]);
  return (
    <Banner
      buttonChildren={
        <SquareButton
          disabled={isDeriving}
          eventName="Assessment page - Stop feedback style recording"
          kind="tertiary"
          size="s"
          type="button"
          onClick={() => {
            onStopRecording();
          }}
        >
          {t("assessment.recording_style_stop")}
        </SquareButton>
      }
      className="page-block-width"
      kind="info"
    >
      {t("assessment.recording_style_banner")}
    </Banner>
  );
};

type BannerTextProps = {
  textKey: ResourceKey;
  count: number;
  total: number;
};

const BannerText: FC<BannerTextProps> = ({ textKey, count, total }) => t(textKey, { count, total });

type AssessmentBannerProps = {
  className?: string;
  page: PageType;
};

export const AssessmentBanner: FC<AssessmentBannerProps> = ({ className, page }) => {
  const dispatch = useAppDispatch();
  const submissions = useAppSelector((s) => selectSubmissionsByPageId(s, page.id));
  const assessments = useAppSelector((s) => selectAssessmentsByPageId(s, page.id));
  const assessmentInfo = useAppSelector((s) => selectAssessmentInfoForPage(s, page.id));
  const { canAuthorSection } = useCapabilities();
  const isAssessing = useIsAssessing();
  const isUnassessable = useAppSelector((s) => selectIsUnassessablePage(s, page));
  const isArchived = useAppSelector(selectCurrentSectionIsArchived);
  const { setQueryParams } = useQueryParamsActions();
  const secondaryOverlay = useQueryParam(SECONDARY_OVERLAY_QUERY_PARAM);
  const shouldShowAssessmentSummary = secondaryOverlay === SecondaryOverlay.AssessmentSummary;

  const isRecordingAssessmentStyle =
    useAppSelector(selectIsRecordingFeedbackStyle) && isAssessmentAIFeedbackStyleEnabled();
  const { count: officialSubmissionsCount, total: totalSubmissionsCount } =
    useAssessedCountData(page);
  const { startAssessing: onAssess } = useAssessmentMode(page);

  const officialSubmissions = useListSelector((s) =>
    selectOfficialFullSubmissionsByPageId(s, page?.id),
  );

  const isAllAssessed = officialSubmissions.length === totalSubmissionsCount;

  const defaultBannerProps: BannerProps = {
    className: "page-block-width",
    Icon: page.assessments_published_at ? undefined : AssessPointsMarkerIcon,
    kind: "success",
    children: "",
  };
  const context = isGroupPage(page) ? "group" : "individual";

  const showAssessmentBanner =
    assessmentInfo?.status !== AssessmentStatus.NOT_ASSESSABLE &&
    canAuthorSection &&
    submissions.length !== 0 &&
    (isGroupPage(page) || isIndividualPage(page)) &&
    !isUnassessable;

  // Banners that are shown when out of assessment mode. Archived sections can't enter assessment
  // mode, so we hide all banners that act as an entry point to start/resume/edit assessing.
  const showSubmissionCollectedBanner = !page.assessments_published_at && assessments.length === 0;
  const showAssessmentInProgressBanner = !page.assessments_published_at && assessments.length !== 0;
  const showEditAssessmentBanner = page.assessments_published_at && shouldShowAssessmentSummary;
  const showViewAssessmentsBanner = page.assessments_published_at && !shouldShowAssessmentSummary;

  if (isAssessing) {
    if (!isRecordingAssessmentStyle) return null;
    return (
      <div className={className}>
        <RecordingFeedbackStyleBanner />
      </div>
    );
  }

  if (!showAssessmentBanner) return null;

  if (showSubmissionCollectedBanner) {
    return (
      <div className={className}>
        <Banner
          {...defaultBannerProps}
          actionButtonText={isArchived ? undefined : t("assessment.assess")}
          eventName="Assessment - Start"
          eventProps={{ pageTitle: page.title, pageId: page.id }}
          kind="info"
          onAction={isArchived ? undefined : onAssess}
        >
          {t("assessment.submissions_collected", { context, count: totalSubmissionsCount })}
        </Banner>
      </div>
    );
  }

  if (showAssessmentInProgressBanner) {
    return (
      <div className={className}>
        <Banner
          {...defaultBannerProps}
          actionButtonText={isArchived ? undefined : t("assessment.resume_assessment")}
          eventName="Assessment - Start"
          eventProps={{ pageTitle: page.title, pageId: page.id }}
          onAction={isArchived ? undefined : onAssess}
        >
          <BannerText
            count={officialSubmissionsCount}
            textKey="assessment.assessment_in_progress"
            total={totalSubmissionsCount}
          />
        </Banner>
      </div>
    );
  }

  if (showViewAssessmentsBanner) {
    return (
      <div className={className}>
        <Banner
          {...defaultBannerProps}
          actionButtonText={
            isArchived ? t("assessment.view_assessments") : t("assessment.view_overview")
          }
          eventName="Assessment - View summary"
          eventProps={{ pageTitle: page.title, pageId: page.id }}
          onAction={() => {
            if (isArchived) {
              dispatch(viewStateActions.setSelectedPageTabVariant("responses"));
              return;
            }
            setQueryParams({ [SECONDARY_OVERLAY_QUERY_PARAM]: SecondaryOverlay.AssessmentSummary });
          }}
        >
          <BannerText
            count={officialSubmissionsCount}
            textKey="assessment.assessments_published"
            total={totalSubmissionsCount}
          />
        </Banner>
      </div>
    );
  }

  if (showEditAssessmentBanner) {
    return (
      <div className={className}>
        <Banner
          {...defaultBannerProps}
          actionButtonText={
            isArchived
              ? undefined
              : isAllAssessed
                ? t("assessment.edit_assessments")
                : t("assessment.resume_assessment")
          }
          eventName="Assessment - Edit"
          eventProps={{ pageTitle: page.title, pageId: page.id }}
          onAction={isArchived ? undefined : onAssess}
        >
          <BannerText
            count={officialSubmissionsCount}
            textKey="assessment.assessments_published"
            total={totalSubmissionsCount}
          />
        </Banner>
      </div>
    );
  }

  return null;
};
